import type { SlideProps, SnackbarCloseReason } from '@mui/material';
import { Snackbar, Slide, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import type { SweepNotification } from '../../reducers/notificationsReducer';
import {
  closeNotification,
  removeNotification,
  selectNotifications,
} from '../../reducers/notificationsReducer';
import { NotificationAlert } from './NotificationAlert';
import { useTimeout } from '../../hooks/useTimeout';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import { useCallback } from 'react';
import usePersistentNotifications from './usePersistentNotifications';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

export const Notifications = () => {
  const ephemeralNotifications = useSelector(selectNotifications);
  const persistentNotifications = usePersistentNotifications();
  const allNotifications = [...persistentNotifications, ...ephemeralNotifications];

  const dispatch = useDispatch();
  const { setSweepTimeout } = useTimeout();
  const { navigate } = useSweepNavigate();

  const handleNavigate = useCallback(
    (url: string) => {
      navigate(url);
    },
    [navigate],
  );

  const onClose = (notification: SweepNotification, reason?: SnackbarCloseReason) => {
    if (notification.keepOpen && reason && ['clickaway', 'escapeKeyDown'].includes(reason)) {
      return;
    }

    if (notification.onClose) {
      notification.onClose();
      return;
    }

    dispatch(closeNotification(notification.key));

    setSweepTimeout(() => {
      dispatch(removeNotification(notification.key));
    }, 1000);
  };

  const getAutoHideDuration = (notification: SweepNotification): number | null => {
    if (notification.autoHideDuration !== undefined) {
      return notification.autoHideDuration;
    }
    if (notification.keepOpen) {
      return null;
    }
    return 3000;
  };

  const renderNotification = (notification: SweepNotification) => {
    const autoHideDuration = getAutoHideDuration(notification);
    const hasAutoHide = autoHideDuration !== null;
    const shouldHideClose = notification.hideClose || hasAutoHide;

    return (
      <Snackbar
        key={notification.key}
        data-testid="notification-snackbar"
        sx={{
          position: 'static',
          transform: 'none',
          whiteSpace: 'pre-line',
        }}
        open={!notification.dismissed}
        TransitionComponent={SlideTransition}
        autoHideDuration={autoHideDuration}
        onClose={(event, reason) => onClose(notification, reason)}
      >
        <NotificationAlert
          onClose={() => onClose(notification)}
          severity={notification.variant}
          message={notification.message}
          action={
            notification.action
              ? {
                  actionText: notification.action.actionText,
                  onActionClick: () =>
                    notification.action?.reduxAction && dispatch(notification.action.reduxAction),
                }
              : undefined
          }
          details={notification.details}
          isVariant={false}
          linkUrl={notification.linkUrl}
          linkText={notification.linkText}
          linkOpenInNewTab={notification.linkOpenInNewTab}
          onNavigate={handleNavigate}
          hideClose={shouldHideClose}
        />
      </Snackbar>
    );
  };

  return (
    <Stack
      direction="column-reverse"
      gap={1}
      sx={{
        position: 'fixed',
        zIndex: 1502,
        bottom: '16px',
        right: '16px',
        alignItems: 'flex-end',
      }}
    >
      {allNotifications.map((notification) => renderNotification(notification))}
    </Stack>
  );
};
